import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Dropdown, Row, Col } from 'antd'
import { MoreOutlined, CheckOutlined, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons'
import { renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { Causal, AllCausal, AvailableOnlyCausal, MovementsSelectValue } from '../../../Constants/Configuration'
import { TableSortingAscend, TableSortingDescend } from '../../../Constants/TableConfiguration'
import { setFilterCausal, setSortInfo } from '../../../Slices/Kiosk/MovementsAvailabilitiesSlice'
import { useAppDispatch, useAppSelector } from '../../../hooks'

function MovementsAvailabilitiesAdditionalMenu(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const enableMovements = useAppSelector(state => state.present.movementsAvailabilities.value.enableMovements)
  const dataSource = useAppSelector(state =>
    enableMovements === MovementsSelectValue
      ? state.present.articleDetail.articleSelected.movements
      : state.present.articleDetail.articleSelected.availabilities,
  )
  const sortInfo = useAppSelector(state => state.present.movementsAvailabilities.value.sortInfo)
  const currentCausal = useAppSelector(state => state.present.movementsAvailabilities.value.filterCausal)

  const [dropdownVisible, setDropdownVisible] = useState(false)

  const sortedInfo = sortInfo || {}

  const causalsValues = useMemo(() => {
    const causals = []
    if (dataSource && dataSource.length !== 0) {
      dataSource.forEach(element => {
        if (element.causal && causals.indexOf(element.causal) === -1) {
          causals.push(element.causal)
        }
      })
    }

    return causals
  }, [dataSource])

  const openDropdown = useCallback(
    e => {
      stopPropagation(e)
      setDropdownVisible(!dropdownVisible)
    },
    [dropdownVisible],
  )

  const hideDropdown = useCallback(e => {
    setDropdownVisible(false)
  }, [])

  const setAscendSorting = useCallback(
    e => {
      dispatch(setSortInfo({ order: TableSortingAscend, columnKey: Causal }))
      setDropdownVisible(!dropdownVisible)
    },
    [dropdownVisible, dispatch],
  )

  const setDescendSorting = useCallback(
    e => {
      dispatch(setSortInfo({ order: TableSortingDescend, columnKey: Causal }))
      setDropdownVisible(!dropdownVisible)
    },
    [dropdownVisible, dispatch],
  )

  const resetSortOrder = useCallback(
    e => {
      dispatch(setSortInfo(null))
      setDropdownVisible(!dropdownVisible)
    },
    [dropdownVisible, dispatch],
  )

  const showAllCausal = useCallback(() => {
    dispatch(setFilterCausal(AllCausal))
    setDropdownVisible(!dropdownVisible)
    props.update()
  }, [dropdownVisible, dispatch, props])

  const showAvailableOnly = useCallback(() => {
    dispatch(setFilterCausal(currentCausal === AvailableOnlyCausal ? AllCausal : AvailableOnlyCausal))
    setDropdownVisible(!dropdownVisible)
    props.update()
  }, [dropdownVisible, dispatch, currentCausal, props])

  const causalFilter = useCallback(
    causal => {
      dispatch(setFilterCausal(causal === currentCausal ? AllCausal : causal))
      setDropdownVisible(!dropdownVisible)
      props.update()
    },
    [dropdownVisible, dispatch, currentCausal, props],
  )

  useEffect(() => {
    if (dropdownVisible) {
      window.addEventListener('click', hideDropdown)
    } else {
      window.removeEventListener('click', hideDropdown)
    }
    return () => {
      window.removeEventListener('click', hideDropdown)
    }
  }, [dropdownVisible, hideDropdown])

  const downSorting = useMemo(() => {
    return sortedInfo.columnKey === Causal && sortedInfo.order === TableSortingDescend
  }, [sortedInfo.columnKey, sortedInfo.order])
  const upSorting = useMemo(() => {
    return sortedInfo.columnKey === Causal && sortedInfo.order === TableSortingAscend
  }, [sortedInfo.columnKey, sortedInfo.order])

  const menu = useMemo(() => {
    return {
      className: 'additional-menu',
      onClick: ({ domEvent }) => stopPropagation(domEvent),
      onSelect: ({ key, keyPath, domEvent }) => {},
      items: [
        {
          key: 'order',
          icon: <CheckOutlined className='v-hidden' />,
          className: 'bordered',
          label: (
            <>
              {renderTranslation(language, TranslationKeys.tableAvailabilitiesDotsMenuOrder)}
              {upSorting ? (
                <CaretUpOutlined className='custom-sort-up active f-right' onClick={resetSortOrder} />
              ) : (
                <CaretUpOutlined className={'custom-sort-up f-right'} onClick={setAscendSorting} />
              )}
              {downSorting ? (
                <CaretDownOutlined className='custom-sort-down active f-right' onClick={resetSortOrder} />
              ) : (
                <CaretDownOutlined className='custom-sort-down f-right' onClick={setDescendSorting} />
              )}
            </>
          ),
        },
        {
          key: 'all-causal',
          icon: <CheckOutlined className={currentCausal === AllCausal ? '' : 'v-hidden'} />,
          onClick: showAllCausal,
          label: renderTranslation(language, TranslationKeys.tableAvailabilitiesDotsMenuAllCausal),
        },
        enableMovements === MovementsSelectValue
          ? {
              key: 'available-only-causal',
              icon: <CheckOutlined className={currentCausal === AvailableOnlyCausal ? '' : 'v-hidden'} />,
              onClick: showAvailableOnly,
              label: renderTranslation(language, TranslationKeys.tableAvailabilitiesDotsMenuAvailableCausal),
            }
          : null,
        {
          key: 'causal-submenu',
          icon: <CheckOutlined className='v-hidden' />,
          label: renderTranslation(language, TranslationKeys.tableAvailabilitiesDotsMenuCausalSelection),
          children: causalsValues.map(causal => {
            return {
              key: causal,
              icon: <CheckOutlined style={currentCausal === causal ? {} : { visibility: 'hidden' }} />,
              onClick: e => {
                causalFilter(causal)
              },
              label: causal,
            }
          }),
        },
      ],
    }
  }, [
    causalFilter,
    causalsValues,
    currentCausal,
    downSorting,
    enableMovements,
    language,
    resetSortOrder,
    setAscendSorting,
    setDescendSorting,
    showAllCausal,
    showAvailableOnly,
    upSorting,
  ])

  return (
    <Dropdown className='additional-availabilities-menu-dropdown' menu={menu} open={dropdownVisible}>
      <Row>
        <Col flex='auto'>{props.children}</Col>
        <Col flex='15px'></Col>
        <MoreOutlined
          className={'dots-' + props.label + ' dots-icon dots-special-movement'}
          style={{
            top: 'calc(50% - 5px)',
            right: 3,
            transform: 'translateY(-25%)',
            position: 'absolute',
            pointerEvents: 'auto',
            fontSize: '1.1em',
          }}
          onClick={openDropdown}
        />
      </Row>
    </Dropdown>
  )
}

export default MovementsAvailabilitiesAdditionalMenu
